import { iTokenUser } from '@/atoms/user.atom';
import { isTauri } from '@/lib/offline/isTauri';
import { getBaseDomain } from './domain';

// In Tauri the page origin is `tauri://localhost`, so a `.skygis.app`-domained
// cookie can't be set/read. Persist the token in localStorage instead and send
// it as `Authorization: Bearer` (the API authorizer accepts that).
const TAURI_TOKEN_KEY = 'tauri.authToken';

// @ts-ignore
export const TOKEN_KEY = import.meta.env.VITE_LOCAL_STORAGE_TOKEN_KEY || '@actegon.gis';
export const REFRESH_TOKEN_KEY = import.meta.env.VITE_LOCAL_STORAGE_REFRESH_TOKEN_KEY || '@actegon.gis.refresh';

const getCookieValue = (name: string): string | null => {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  return match.split('=').slice(1).join('=') || null;
};

const getRootDomain = () => {
  const parts = getBaseDomain().split('.');
  return parts.slice(-2).join('.');
};

const setCookie = (name: string, value: string | null, maxAge = 60 * 60 * 24 * 30) => {
  const rootDomain = getRootDomain();
  if (value) {
    document.cookie = `${name}=${value}; Domain=.${rootDomain}; Path=/; Secure; SameSite=Lax; Max-Age=${maxAge}`;
  } else {
    document.cookie = `${name}=; Domain=.${rootDomain}; Path=/; Secure; SameSite=Lax; Max-Age=0`;
  }
};

export const getToken = (): string | null => {
  if (isTauri()) return localStorage.getItem(TAURI_TOKEN_KEY);

  const cookieValue = getCookieValue(TOKEN_KEY);
  if (cookieValue) return cookieValue;
  // Fallback to localStorage for migration
  const lsValue = localStorage.getItem(TOKEN_KEY);
  if (lsValue) {
    setCookie(TOKEN_KEY, lsValue);
    localStorage.removeItem(TOKEN_KEY);
    return lsValue;
  }
  return null;
};

export const setToken = (token: string | null) => {
  if (isTauri()) {
    if (token) localStorage.setItem(TAURI_TOKEN_KEY, token);
    else localStorage.removeItem(TAURI_TOKEN_KEY);
    return;
  }
  // Clear any cookie set on the exact hostname (no domain) to prevent shadowing
  document.cookie = `${TOKEN_KEY}=; Path=/; Secure; SameSite=Lax; Max-Age=0`;
  setCookie(TOKEN_KEY, token);
  localStorage.removeItem(TOKEN_KEY);
};

export const removeToken = () => {
  if (isTauri()) {
    localStorage.removeItem(TAURI_TOKEN_KEY);
    return;
  }
  setCookie(TOKEN_KEY, null);
  localStorage.removeItem(TOKEN_KEY);
};

export const getRefreshToken = () => {
  const cookieValue = getCookieValue(REFRESH_TOKEN_KEY);
  if (cookieValue) return cookieValue;
  const lsValue = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (lsValue) {
    setCookie(REFRESH_TOKEN_KEY, lsValue);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    return lsValue;
  }
  return null;
};

export const setRefreshToken = (token: string) => {
  setCookie(REFRESH_TOKEN_KEY, token);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
export const parseJwt = (token: string): Partial<iTokenUser> & { exp: number } => {
  token = token?.trim?.();
  if (!token) return {} as any;
  const base64Url = token.split('.')[1] || '';
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  try {
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT', error, jsonPayload, token);
    return {} as any;
  }
};

export const getStoredUser = () => {
  const token = getToken();
  if (!token) return null;
  try {
    return parseJwt(token) as iTokenUser;
  } catch (error) {
    return null;
  }
};
